import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import PropTypes from 'prop-types';
import xml2js from 'xml2js';

import { Accordion, AccordionSummary, Box, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Alert from '@material-ui/lab/Alert';

import CircularProgress from 'components/shared/CircularProgress';

import { connect } from 'react-redux';
import { fetchInfoRequest } from '../redux/actionCreators';
import * as selectors from '../redux/selectors';

import Dac7Data from './Dac7Data';

const parseXml = async (xml) => {
  let xmlData;
  const parser = new xml2js.Parser({ trim: true, explicitArray: false, mergeAttrs: true });
  try {
    xmlData = await parser.parseStringPromise(xml);
  } catch (error) {
    throw new Error(`Error parsing xml: ${error}`);
  }
  if (
    xmlData?.root?.['ku:Blankett'] !== undefined &&
    !Array.isArray(xmlData?.root?.['ku:Blankett'])
  ) {
    xmlData.root['ku:Blankett'] = [xmlData?.root?.['ku:Blankett']];
  }

  return xmlData?.root?.['ku:Blankett'];
};

const createUserData = (forms) => {
  if (!Array.isArray(forms) || forms.length === 0) return [];

  const getSumsPerCurrency = (values, valutakoder) => {
    const uniqueValutakoder = [...new Set(valutakoder.filter((kod) => kod !== ''))];

    const sumsPerCurrency = uniqueValutakoder.map((kod) => {
      const sum = values.reduce((total, value, index) => {
        if (valutakoder[index] === kod) {
          return total + value;
        }
        return total;
      }, 0);

      return {
        currency: kod,
        sum,
      };
    });

    return sumsPerCurrency;
  };

  return forms.map((form) => {
    const blankettinnehall = form?.['ku:Blankettinnehall'];
    const ku93 = blankettinnehall?.['ku:KU93'];
    const inkomsttagareKu93 = ku93?.['ku:InkomsttagareKU93'];

    if (!ku93) throw new Error('createUserData: ku93 is not defined');

    const { userKey } = blankettinnehall.metaData;
    const userType = userKey.split('-')[2];

    return {
      year: Number(ku93['ku:Inkomstar']._),
      userId: Number(blankettinnehall.metaData.userId),
      userType,
      userKey: blankettinnehall.metaData.userKey,
      firstName: inkomsttagareKu93?.['ku:Fornamn']?._ || '',
      lastName: inkomsttagareKu93?.['ku:Efternamn']?._ || '',
      companyName: inkomsttagareKu93?.['ku:OrgNamn']?._ || '',
      nameOrCompanyName: (data) => {
        if (data.userType === 'LEGAL' && data.companyName) {
          return data.companyName;
        }
        return `${data.firstName}${String.fromCharCode(160)}${data.lastName}`;
      },
      propertyDesignation: ku93?.['ku:FastighetsbeteckningFE']?._ || '',
      propertyStreetAddress: ku93?.['ku:Gatuadress1FE']?._ || '',
      propertyPostalCode: ku93?.['ku:PostnummerFE']?._ || '',
      propertyCity: ku93?.['ku:PostortFE']?._ || '',
      cottageId: Number(ku93['ku:Specifikationsnummer']._),
      ersattningQ1: Number(ku93?.['ku:ErsattningBeloppQ1']?._) || 0,
      ersattningQ2: Number(ku93?.['ku:ErsattningBeloppQ2']?._) || 0,
      ersattningQ3: Number(ku93?.['ku:ErsattningBeloppQ3']?._) || 0,
      ersattningQ4: Number(ku93?.['ku:ErsattningBeloppQ4']?._) || 0,
      valutakodErsattningQ1: ku93?.['ku:ValutakodErsattningQ1']?._ || '',
      valutakodErsattningQ2: ku93?.['ku:ValutakodErsattningQ2']?._ || '',
      valutakodErsattningQ3: ku93?.['ku:ValutakodErsattningQ3']?._ || '',
      valutakodErsattningQ4: ku93?.['ku:ValutakodErsattningQ4']?._ || '',
      totalErsattning: (data) => {
        const valutakoder = [
          data.valutakodErsattningQ1,
          data.valutakodErsattningQ2,
          data.valutakodErsattningQ3,
          data.valutakodErsattningQ4,
        ];
        const values = [data.ersattningQ1, data.ersattningQ2, data.ersattningQ3, data.ersattningQ4];
        return getSumsPerCurrency(values, valutakoder);
      },
      avgifterQ1: Number(ku93?.['ku:AvgifterBeloppQ1']?._) || 0,
      avgifterQ2: Number(ku93?.['ku:AvgifterBeloppQ2']?._) || 0,
      avgifterQ3: Number(ku93?.['ku:AvgifterBeloppQ3']?._) || 0,
      avgifterQ4: Number(ku93?.['ku:AvgifterBeloppQ4']?._) || 0,
      valutakodAvgifterQ1: ku93?.['ku:ValutakodAvgifterQ1']?._ || '',
      valutakodAvgifterQ2: ku93?.['ku:ValutakodAvgifterQ2']?._ || '',
      valutakodAvgifterQ3: ku93?.['ku:ValutakodAvgifterQ3']?._ || '',
      valutakodAvgifterQ4: ku93?.['ku:ValutakodAvgifterQ4']?._ || '',
      totalAvgifter: (data) => {
        const valutakoder = [
          data.valutakodAvgifterQ1,
          data.valutakodAvgifterQ2,
          data.valutakodAvgifterQ3,
          data.valutakodAvgifterQ4,
        ];
        const values = [data.avgifterQ1, data.avgifterQ2, data.avgifterQ3, data.avgifterQ4];
        return getSumsPerCurrency(values, valutakoder);
      },
      antalTransaktionerQ1: Number(ku93?.['ku:AntalTransaktionerQ1']?._) || 0,
      antalTransaktionerQ2: Number(ku93?.['ku:AntalTransaktionerQ2']?._) || 0,
      antalTransaktionerQ3: Number(ku93?.['ku:AntalTransaktionerQ3']?._) || 0,
      antalTransaktionerQ4: Number(ku93?.['ku:AntalTransaktionerQ4']?._) || 0,
      totalAntalTransaktioner: (data) => {
        return (
          data.antalTransaktionerQ1 +
          data.antalTransaktionerQ2 +
          data.antalTransaktionerQ3 +
          data.antalTransaktionerQ4
        );
      },
      antalUthyrdaDagar: Number(ku93?.['ku:AntalUthyrdaDagarFE']?._) || 0,
    };
  });
};

const Dac7ListItem = ({ year, fetchInfoReq, info, isLoading, error }) => {
  const [expanded, setExpanded] = useState(false);
  const [userData, setUserData] = useState(null);

  const isError = error && Object.keys(error).length > 0;

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
    if (isExpanded && !userData && !info && !isLoading && !isError) {
      fetchInfoReq(year);
    }
  };

  useEffect(() => {
    if (info) {
      const processInfo = async () => {
        const forms = await parseXml(info);
        if (forms) {
          const parsedData = createUserData(forms);
          setUserData(parsedData);
        } else {
          setUserData('no-data-found');
        }
      };
      processInfo();
    }
  }, [info]);

  const xmlData = info;

  return (
    <Accordion expanded={expanded} onChange={handleAccordionChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">{year}</Typography>
      </AccordionSummary>
      {isLoading && <CircularProgress />}
      {isError && (
        <Alert severity="error" elevation={3}>
          <FormattedMessage
            id="dac7.fetch.error"
            defaultMessage="An error occurred while fetching data"
          />
        </Alert>
      )}
      {expanded && userData && (
        <Box display="flex" flexDirection="column">
          <Dac7Data userData={userData} xmlData={xmlData} />
        </Box>
      )}
    </Accordion>
  );
};

Dac7ListItem.propTypes = {
  year: PropTypes.number.isRequired,
  fetchInfoReq: PropTypes.func.isRequired,
  info: PropTypes.string,
  isLoading: PropTypes.bool,
  error: PropTypes.shape({}),
};

const mapStateToProps = (state, ownProps) => ({
  error: selectors.error(state, ownProps.year),
  info: selectors.getInfoByYear(state, ownProps.year),
  isLoading: selectors.isLoading(state, ownProps.year),
});

const mapDispatchToProps = (dispatch) => ({
  fetchInfoReq: (year) => dispatch(fetchInfoRequest(year)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Dac7ListItem);
